/**
 * ColumnPickerPanel — F12.E T12.E.9.
 *
 * Panel wyboru widocznych kolumn tabeli rekordów.
 * Stan przechowywany w localStorage per moduł (klucz: `module-columns-<moduleId>`).
 *
 * Funkcje:
 *  - Toggle widoczności kolumny (checkbox)
 *  - Drag & drop reorder (@dnd-kit/sortable)
 *  - Reset do domyślnych (showInList=true per pole)
 *
 * Eksportuje też hook `useColumnConfig` do pobierania aktywnych kolumn w ModuleRecordsPage.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Eye, EyeOff, GripVertical, RotateCcw } from 'lucide-react';

import type { ModuleField } from '@/components/fields/FieldRegistry';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ColumnConfig {
  slug: string;
  visible: boolean;
}

interface Props {
  moduleId: string;
  fields: ModuleField[];
  onClose: () => void;
}

// ─── localStorage helpers ─────────────────────────────────────────────────────

function storageKey(moduleId: string) {
  return `module-columns-${moduleId}`;
}

function loadConfig(moduleId: string, fields: ModuleField[]): ColumnConfig[] {
  try {
    const raw = localStorage.getItem(storageKey(moduleId));
    if (raw) {
      const parsed = JSON.parse(raw) as ColumnConfig[];
      // Uzupełnij o nowe pola których nie ma w zapisie
      const savedSlugs = new Set(parsed.map((c) => c.slug));
      const newFields = fields
        .filter((f) => !savedSlugs.has(f.slug))
        .map((f) => ({ slug: f.slug, visible: f.showInList }));
      // Usuń pola które już nie istnieją
      const validSlugs = new Set(fields.map((f) => f.slug));
      const filtered = parsed.filter((c) => validSlugs.has(c.slug));
      return [...filtered, ...newFields];
    }
  } catch {
    // ignore
  }
  // Default: porządek wg sortOrder, visible = showInList
  return fields.map((f) => ({ slug: f.slug, visible: f.showInList }));
}

function saveConfig(moduleId: string, config: ColumnConfig[]) {
  try {
    localStorage.setItem(storageKey(moduleId), JSON.stringify(config));
  } catch {
    // ignore (np. private mode bez storage)
  }
}

/** Hook do pobierania aktywnych kolumn z localStorage — używany przez ModuleRecordsPage. */
export function useColumnConfig(moduleId: string, fields: ModuleField[]): ModuleField[] {
  const config = loadConfig(moduleId, fields);
  const slugOrder = config.filter((c) => c.visible).map((c) => c.slug);
  return slugOrder
    .map((slug) => fields.find((f) => f.slug === slug))
    .filter((f): f is ModuleField => !!f);
}

// ─── Sortable Row ─────────────────────────────────────────────────────────────

interface SortableRowProps {
  col: ColumnConfig;
  fieldName: string;
  onToggle: (slug: string) => void;
}

function SortableRow({ col, fieldName, onToggle }: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: col.slug,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors ${
        isDragging ? 'bg-bg-elevated shadow-md' : 'hover:bg-bg-secondary'
      }`}
    >
      {/* Drag handle */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab touch-none text-text-muted hover:text-text-secondary active:cursor-grabbing"
        aria-label="Przesuń"
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Toggle visibility */}
      <button
        type="button"
        onClick={() => onToggle(col.slug)}
        className={`flex items-center gap-2 flex-1 text-left text-sm ${
          col.visible ? 'text-text-primary' : 'text-text-muted line-through'
        }`}
      >
        {col.visible ? (
          <Eye className="h-3.5 w-3.5 text-accent-500 shrink-0" />
        ) : (
          <EyeOff className="h-3.5 w-3.5 shrink-0" />
        )}
        {fieldName}
      </button>
    </div>
  );
}

// ─── Main Panel ───────────────────────────────────────────────────────────────

export function ColumnPickerPanel({ moduleId, fields, onClose }: Props) {
  const { t } = useTranslation();
  const [config, setConfig] = useState<ColumnConfig[]>(() => loadConfig(moduleId, fields));

  // Zapisuj do localStorage przy każdej zmianie
  useEffect(() => {
    saveConfig(moduleId, config);
  }, [moduleId, config]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setConfig((prev) => {
      const oldIdx = prev.findIndex((c) => c.slug === active.id);
      const newIdx = prev.findIndex((c) => c.slug === over.id);
      return arrayMove(prev, oldIdx, newIdx);
    });
  }

  function toggleVisibility(slug: string) {
    setConfig((prev) => prev.map((c) => (c.slug === slug ? { ...c, visible: !c.visible } : c)));
  }

  function resetToDefaults() {
    const defaults = fields.map((f) => ({ slug: f.slug, visible: f.showInList }));
    setConfig(defaults);
  }

  const fieldMap = new Map(fields.map((f) => [f.slug, f.name]));

  return (
    <div
      className="absolute right-0 top-full z-50 mt-1 w-64 rounded-xl border border-border-primary bg-bg-primary shadow-lg"
      data-testid="column-picker-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border-primary px-3 py-2">
        <span className="text-sm font-medium text-text-primary">
          {t('tenant.modules.records.columnPicker.title')}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-text-muted hover:text-text-primary"
        >
          ✕
        </button>
      </div>

      {/* Lista kolumn z drag & drop */}
      <div className="max-h-72 overflow-y-auto py-1">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={config.map((c) => c.slug)} strategy={verticalListSortingStrategy}>
            {config.map((col) => (
              <SortableRow
                key={col.slug}
                col={col}
                fieldName={fieldMap.get(col.slug) ?? col.slug}
                onToggle={toggleVisibility}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>

      {/* Footer */}
      <div className="border-t border-border-primary px-3 py-2">
        <button
          type="button"
          onClick={resetToDefaults}
          className="flex items-center gap-1 text-xs text-text-muted hover:text-text-primary"
        >
          <RotateCcw className="h-3 w-3" />
          {t('tenant.modules.records.columnPicker.reset')}
        </button>
      </div>
    </div>
  );
}
